import { defineMessage, type IntlShape, type MessageDescriptor } from "react-intl";
import { Ar, io, Ord, pipe } from "../prelude";

const levelOrder = {
  preprimary: 0,
  primary: 1,
  "lower secondary": 2,
  "upper secondary": 3,
  tertiary: 4,
};

export const Level = io.keyof(levelOrder);
export type Level = io.TypeOf<typeof Level>;

export const getLevelIndex = (k: Level) => levelOrder[k];

export const ordLevel = Ord.contramap((level: Level) => getLevelIndex(level))(Ord.ordNumber);
export const ordLevelReverse = Ord.contramap((level: Level) => -getLevelIndex(level))(Ord.ordNumber);

const levelLookup: Record<Level, MessageDescriptor> = {
  preprimary: defineMessage({ id: "preprimary", defaultMessage: "Pre-primary" }),
  primary: defineMessage({ id: "primary", defaultMessage: "Primary" }),
  "lower secondary": defineMessage({ id: "lower secondary", defaultMessage: "Lower Secondary" }),
  "upper secondary": defineMessage({ id: "upper secondary", defaultMessage: "Upper Secondary" }),
  tertiary: defineMessage({ id: "tertiary", defaultMessage: "Tertiary" }),
};
export const getLevelName = (intl: IntlShape, level: Level) => intl.formatMessage(levelLookup[level]);

export function sortLevels<A>(xs: Array<A>, get: (x: A) => Level) {
  return pipe(xs, Ar.sort(Ord.contramap((needle: A) => get(needle))(ordLevel)));
}

// -----------------------------------------------------------------------------
// Level, but with attendance/completion

export const LevelAC = io.keyof({
  ever_attend: null,
  prim_completion: null,
  lsec_completion: null,
  upsec_completion: null,
  postsec_attendance: null,
});
export type LevelAC = io.TypeOf<typeof LevelAC>;

const levelACOrder = {
  ever_attend: 0,
  prim_completion: 1,
  lsec_completion: 2,
  upsec_completion: 3,
  postsec_attendance: 4,
};
export const getLevelACIndex = (k: LevelAC) => levelACOrder[k];
export const ordLevelAC = Ord.contramap((level: LevelAC) => getLevelACIndex(level))(Ord.ordNumber);

const levelACLookup: Record<LevelAC, MessageDescriptor> = {
  ever_attend: defineMessage({ id: "ever_attend", defaultMessage: "Ever been to school" }),
  prim_completion: defineMessage({ id: "prim_completion", defaultMessage: "Primary completion" }),
  lsec_completion: defineMessage({ id: "lsec_completion", defaultMessage: "Lower secondary completion" }),
  upsec_completion: defineMessage({ id: "upsec_completion", defaultMessage: "Upper secondary completion" }),
  postsec_attendance: defineMessage({ id: "postsec_attendance", defaultMessage: "Post secondary attendance" }),
};
export const getLevelACName = (intl: IntlShape, level: LevelAC) => intl.formatMessage(levelACLookup[level]);
